import { hierarchy, pack } from "d3"

import { type VisualizerLens } from "./uploadedConversationsVisualizerDataset"
import {
  insetOverlayAnchor,
  resolveChaosAnchor,
} from "./uploadedConversationsVisualizerOverlayDotGeometry"
import { type OverlayDotBlueprint } from "./uploadedConversationsVisualizerOverlayDots"
import {
  hashString32,
  unitFloatFromSeed,
} from "./uploadedConversationsVisualizerOverlayHash"
import {
  clampToPlate,
  PLATE_DOMAIN_RADIUS,
} from "./uploadedConversationsVisualizerParticles"

const GOLDEN_ANGLE_RADIANS = Math.PI * (3 - Math.sqrt(5))
const ISSUE_ANCHOR_INSET_SCALE = 0.92
const PACK_DOMAIN_SCALE = PLATE_DOMAIN_RADIUS * 1.8
const CHILD_ISSUE_RADIUS_SHARE = 0.82

interface FailurePackNode {
  id: string
  weight?: number
  children?: FailurePackNode[]
}

/** The hierarchy owns the display geometry; PCA issue coordinates remain data, not membership rules. */
export function resolveFailureIssueDots(
  lens: VisualizerLens,
  visualizationKey: string,
) {
  const failureOutput = lens.failureOutput
  if (!failureOutput || lens.bucketAssignments.length === 0) {
    return []
  }

  const assignmentsById = new Map(
    lens.bucketAssignments.map((assignment, index) => [
      assignment.bucketKey,
      { assignment, index },
    ]),
  )
  const assignedChildIds = new Set<string>()
  const parentNodes: FailurePackNode[] = failureOutput.parents.map(
    (parent) => ({
      id: parent.group_id,
      children: (parent.child_cluster_ids ?? []).flatMap((childId) => {
        const entry = assignmentsById.get(childId)
        if (!entry || assignedChildIds.has(childId)) {
          return []
        }
        assignedChildIds.add(childId)
        return [
          {
            id: childId,
            weight: Math.max(entry.assignment.conversations.length, 1),
          },
        ]
      }),
    }),
  )
  for (const [childId, entry] of assignmentsById) {
    if (!assignedChildIds.has(childId)) {
      parentNodes.push({
        id: childId,
        children: [
          {
            id: childId,
            weight: Math.max(entry.assignment.conversations.length, 1),
          },
        ],
      })
    }
  }

  const root = hierarchy<FailurePackNode>({ id: "root", children: parentNodes })
    .sum((node) => node.weight ?? 0)
    .sort((left, right) => (right.value ?? 0) - (left.value ?? 0))
  const packed = pack<FailurePackNode>().size([1, 1]).padding(0.02)(root)

  return packed.leaves().flatMap((child) => {
    const entry = assignmentsById.get(child.data.id)
    if (!entry) {
      return []
    }
    const { assignment, index: sliceIndex } = entry
    const conversations = [...assignment.conversations].sort((left, right) => {
      const leftId = left.dotId ?? left.id
      const rightId = right.dotId ?? right.id
      return (
        hashString32(leftId) - hashString32(rightId) ||
        leftId.localeCompare(rightId)
      )
    })
    const rotation =
      unitFloatFromSeed(
        hashString32(`${visualizationKey}:${child.data.id}`),
        0x9e3779b9,
      ) *
      Math.PI *
      2

    return conversations.map((conversation, issueIndex) => {
      const dotId = conversation.dotId ?? conversation.id
      const hash = hashString32(`${visualizationKey}:${dotId}`)
      const angle = rotation + issueIndex * GOLDEN_ANGLE_RADIANS
      const radius =
        conversations.length === 1
          ? 0
          : Math.sqrt((issueIndex + 0.5) / conversations.length) *
            child.r *
            CHILD_ISSUE_RADIUS_SHARE
      const point = clampToPlate(
        0.5 + (child.x - 0.5 + Math.cos(angle) * radius) * PACK_DOMAIN_SCALE,
        0.5 + (child.y - 0.5 + Math.sin(angle) * radius) * PACK_DOMAIN_SCALE,
      )
      const anchor = insetOverlayAnchor(point, ISSUE_ANCHOR_INSET_SCALE)
      const amp = 0.58 + ((hash >>> 7) % 100) / 160
      const chaosAnchor = resolveChaosAnchor(anchor.u, anchor.v, amp, hash)
      return {
        amp,
        anchorU: anchor.u,
        anchorV: anchor.v,
        bucketLabel: assignment.label,
        chaosU: chaosAnchor.u,
        chaosV: chaosAnchor.v,
        conversation,
        dotId,
        frequencyA: 0.8 + ((hash >>> 10) % 130) / 100,
        frequencyB: 0.9 + ((hash >>> 16) % 130) / 100,
        nodalStrength: 0.64,
        phaseA: ((hash >>> 2) % 6280) / 1000,
        phaseB: ((hash >>> 12) % 6280) / 1000,
        sliceIndex,
      } satisfies OverlayDotBlueprint
    })
  })
}
